import * as React from 'react';
import {faTimes} from '@fortawesome/free-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {useEffect, useMemo, useState} from 'react';

import Button from '../../../ui/Buttons/Button';
import ImpressionTracker from '../../../analytics/impression-tracker/ImpressionTracker';
import {addItem} from '../../compare/CompareFunctions';
import {Alerts} from '../../../ui/Alerts/Alerts';
import {AlternateProducts} from '../AlternateProducts';
import {Badges} from '../Badges';
import {ExceptionIcons} from '../../../orders/exception-icons/ExceptionIcons';
import {GaItemListName, gaSelectItem} from '../../../../client/ga/ga-ecommerce.functions';
import {Item, OrderableItem} from '../../item.class';
import {ItemsService} from '../../../../client/items/items.service';
import {ItemToAdd} from '../../../order-items/order-items.class';
import {ModalBackdrop} from '../../../ui/modals/ModalBackdrop';
import {OrderItemsWorkflow} from '../../../../client/order-items/order-items.workflow';
import {OrdersService} from '../../../../client/orders/orders.service';
import {Price} from '../../../pricing/Price';
import {Spinner} from '../../../ui/Spinner/Spinner';
import {Stepper} from '../../../ui/forms/Stepper/Stepper';
import {StockStatus} from '../../stock-status/StockStatus';
import {TechSpecs} from '../TechSpecs';
import {UrlClass} from '../../../tools/UrlClass';
import {User} from '../../../users/user.class';
import {useService} from '../../../react/ServiceContext';

interface ItemQuickViewModalProps {
    disableEcommerceFunctions?: boolean;
    itemNumber: string;
    onCatalogPage?: boolean;
    onHide: () => void;
    pageLabel?: string;
    show: boolean;
    user: User;
}

export const itemQuickviewComponentName = `item-quickview-component`;
export const ItemQuickViewModal = ({
    disableEcommerceFunctions,
    itemNumber,
    onCatalogPage,
    onHide,
    pageLabel,
    show,
    user,
}: ItemQuickViewModalProps) => {
    const [alternateProducts, setAlternateProducts] = useState<OrderableItem[]>();
    const [errorMessage, setErrorMessage] = useState(``);
    const [loadedItem, setLoadedItem] = useState<Item>();
    const [orderQty, setOrderQty] = useState(1);
    const canPurchase = useMemo(() => loadedItem && Item.userCanPurchase(loadedItem, user, undefined), [loadedItem, user]);

    const [isProdDescTabOpen, setIsProdDescTabOpen] = useState(true);
    const [isTechSpecTabOpen, setIsTechSpecTabOpen] = useState(false);
    const [isDetailTabOpen, setIsDetailTabOpen] = useState(false);

    const gaItemListName: GaItemListName = `item_quick_view_modal`;
    const itemsService: ItemsService = useService(`itemsService`);
    const orderItemsWorkflow: OrderItemsWorkflow = useService(`orderItemsWorkflow`);
    const ordersService: OrdersService = useService(`ordersService`);

    /**
     * Handle events after item data is loaded in the modal
     * @param item
     */
    const handleItemLoaded = (item: Item) => {
        setLoadedItem(item);

        // Record analytics (assume display of quick view corresponds to a click)
        gaSelectItem([
            {
                dimension16: ordersService.currentOrderNumber,
                index: 1,
                item_id: itemNumber,
                item_list_name: gaItemListName,
            },
        ]);

        // If on catalog page, prepare "back" state
        if (onCatalogPage) {
            let returnUrl: string;
            if (pageLabel) {
                returnUrl = `/flyer/full-line-catalog/${pageLabel}?popup=${item.id}`;
            } else {
                returnUrl = UrlClass.addParam(window.location.toString(), 'popup', item.id);
            }
            history.pushState({}, ``, returnUrl);
        }
    };

    /**
     * Load item detail on render
     */
    useEffect(() => {
        if (itemNumber) {
            itemsService
                .getItemDetail(itemNumber)
                .then((item) => {
                    if (item.isdiscontinue) {
                        itemsService
                            .getSubstituteItems(itemNumber)
                            .then((newSubs) => {
                                setAlternateProducts(newSubs);
                                handleItemLoaded(item);
                            })
                            .catch(() => {
                                // error silently
                            });
                    } else {
                        handleItemLoaded(item);
                    }
                })
                .catch((loadError) => {
                    setErrorMessage(loadError.message);
                });
        }
        // Only On Item number changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemNumber]);

    /**
     * Adds items to order
     * @param itemsToAdd - Items to add to the order
     * @private
     */
    const addToOrder = (itemsToAdd: ItemToAdd[]) => {
        orderItemsWorkflow.addToOrderModal(itemsToAdd, itemQuickviewComponentName, true);
    };

    /**
     * Resets the state of the modal before closing
     */
    const onClose = () => {
        // Clear modal state
        setErrorMessage(``);
        setLoadedItem(undefined);

        // Invoke provided onHide()
        onHide();
    };

    /**
     * Template
     */
    return show ? (
        <>
            <ModalBackdrop onClick={onHide} />
            <div className="tw-p-0 tw-h-full tw-right-0 tw-justify-self-center tw-w-full tw-z-[1050] tw-left-0 tw-justify-center tw-top-0 tw-fixed tw-overflow-x-hidden tw-overflow-y-auto tw-max-w-none sm:tw-min-h-[calc(100%-1.75rem)] md:tw-max-w-[920px] md:tw-w-[80vw] tw-block">
                <div className="tw-relative tw-items-center tw-w-full tw-h-full tw-max-w-none tw-max-h-full tw-m-0 tw-top-0 tw-flex md:tw-my-7 md:tw-mx-auto sm:tw-min-h-[calc(100%-3.5rem)] md:tw-max-h-[calc(100%-3.5rem)] md:tw-max-w-[920px] md:tw-w-[80vw] tw-justify-center tw-flex-col">
                    <div className="tw-h-full md:tw-h-auto tw-relative tw-flex tw-flex-col tw-w-full tw-pointer-events-auto tw-bg-white tw-outline-0 tw-rounded-none tw-border-none tw-overflow-hidden tw-max-h-none">
                        <div className="tw-flex tw-items-center tw-text-white tw-px-4 tw-justify-between tw-bg-gray-600 tw-flex-shrink-0">
                            <div className="tw-mb-0">
                                <div className="tw-text-[calc(1.3rem+0.6vw)] tw-font-weight tw-leading-[1.2] tw-mb-0">
                                    Item #{itemNumber}
                                </div>
                            </div>
                            <Button
                                aria-label="Close"
                                onClick={onClose}
                                size="lg"
                                variant="secondary"
                            >
                                <FontAwesomeIcon
                                    color="#e5e5e5"
                                    icon={faTimes}
                                    size="3x"
                                />
                            </Button>
                        </div>
                        <div className="tw-relative tw-p-8 tw-flex tw-flex-col tw-overflow-y-auto">
                            <Alerts
                                message={errorMessage}
                                variant="danger"
                            />
                            {loadedItem ? (
                                <>
                                    {loadedItem.pricingStatus === `Blocked` && (
                                        <Alerts
                                            message="Item has been blocked from ordering per your request"
                                            variant="danger"
                                        />
                                    )}
                                    {loadedItem.pricingStatus === `Convert` && (
                                        <Alerts
                                            message={`Item has been converted to ${Item.prettyItem(
                                                loadedItem.price.error ? loadedItem.price.error : ``,
                                            )} per your request`}
                                            variant="danger"
                                        />
                                    )}
                                    {loadedItem.isdiscontinue && (
                                        <>
                                            <Alerts
                                                message={
                                                    loadedItem.inStock && loadedItem.itemtype !== 'D'
                                                        ? 'Item has been discontinued. Purchase while supplies last!'
                                                        : 'This product has been discontinued and is no longer available.'
                                                }
                                                variant="warning"
                                            />
                                            <AlternateProducts
                                                alternateProducts={alternateProducts}
                                                item={loadedItem}
                                                maxSlides={4}
                                                user={user}
                                            />
                                        </>
                                    )}
                                    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-12 tw-mb-3">
                                        <div className="tw-w-full tw-relative tw-col-span-1 md:tw-col-span-3">
                                            <div className="tw-p-4">
                                                <img
                                                    alt={loadedItem.name}
                                                    className="tw-max-w-full tw-h-auto"
                                                    height="400"
                                                    itemProp="image"
                                                    src={`/ProductImageThumbs400${loadedItem.imagePath || '/noimage.png'}`}
                                                    width="400"
                                                />
                                            </div>
                                        </div>
                                        <div className="tw-w-full tw-relative tw-col-span-1 md:tw-col-span-9">
                                            <div className="tw-mb-3">
                                                <Badges item={loadedItem} />
                                            </div>
                                            <h1 className="tw-border-b tw-text-[calc(1.3rem+0.6vw)] tw-mb-2 tw-font-normal tw-leading-[1.2] tw-pb-2">
                                                {loadedItem.shortdesc}
                                            </h1>
                                            <div className="tw-text-right md:tw-text-left tw-mb-3">
                                                <div>Pkg Qty: {loadedItem.pkgQty}</div>
                                                {!(
                                                    (loadedItem.inStock === false || loadedItem.itemtype === 'D') &&
                                                    loadedItem.isdiscontinue
                                                ) && (
                                                    <Price
                                                        alias="SearchResultItem"
                                                        className="ImpItem_priceLine tw-text-xl tw-mt-1 !tw-font-bold !tw-mb-3"
                                                        doNotShowEachPrice={true}
                                                        item={loadedItem}
                                                        user={user}
                                                    />
                                                )}
                                            </div>
                                            {!loadedItem.isdiscontinue && canPurchase && (
                                                <div className="tw-mb-3">
                                                    <StockStatus
                                                        item={loadedItem}
                                                        unitsOrd={orderQty}
                                                        user={user}
                                                    />
                                                </div>
                                            )}
                                            {loadedItem.itemQtyLimit_i && (
                                                <div className="tw-mb-3">
                                                    Maximum order quantity: {Math.floor(loadedItem.itemQtyLimit_i / loadedItem.pkgQty)}
                                                </div>
                                            )}
                                            {!disableEcommerceFunctions && (
                                                <div className="tw-flex tw-flex-row justify-content-between tw-mt-6">
                                                    <div className="tw-w-[130px]">
                                                        <Stepper
                                                            className="[&_button]:!tw-h-[52px] [&_button]:!tw-w-[52px] [&_input]:!tw-h-[52px] [&_input]:!tw-w-[52px]"
                                                            disabled={loadedItem.isdiscontinue}
                                                            maximum={loadedItem.itemQtyLimit_i}
                                                            onUpdateAllowInvalid={false}
                                                            onUpdate={(qty) => {
                                                                setOrderQty(qty);
                                                            }}
                                                            value={orderQty}
                                                        />
                                                    </div>
                                                    <div className="tw-flex tw-flex-col">
                                                        <Button
                                                            className="tw-px-4 tw-rounded"
                                                            e2e="eCatalog_AddToOrderBtn"
                                                            disabled={!canPurchase}
                                                            onClick={() => {
                                                                addToOrder([
                                                                    {
                                                                        gaEcommerce: {item_list_name: gaItemListName},
                                                                        item: loadedItem.id,
                                                                        unitsOrdered: orderQty,
                                                                    },
                                                                ]);
                                                            }}
                                                            size="lg"
                                                            variant="primary"
                                                        >
                                                            Add to Order
                                                        </Button>
                                                        <Button
                                                            className="!tw-text-right !tw-h-auto !tw-px-0"
                                                            onClick={() => addItem(loadedItem.id, loadedItem.imagePath)}
                                                            variant="link"
                                                        >
                                                            Compare
                                                        </Button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="tw-mb-3">
                                        <ul
                                            className="tw-flex tw-flex-wrap tw-pl-0 tw-mb-6 tw-list-none"
                                            id="itemDetailTabs"
                                            role="tablist"
                                        >
                                            <li
                                                className={`tw-border-b tw-list-none${
                                                    isProdDescTabOpen
                                                        ? ' tw-border-t tw-border-x tw-border-b-white [&_button]:!tw-text-gray-500'
                                                        : ''
                                                }`}
                                            >
                                                <Button
                                                    aria-controls="desc"
                                                    aria-selected="true"
                                                    data-toggle="tab"
                                                    id="desc-tab"
                                                    onClick={() => {
                                                        location.assign('#desc');
                                                        setIsTechSpecTabOpen(false);
                                                        setIsDetailTabOpen(false);
                                                        setIsProdDescTabOpen(true);
                                                    }}
                                                    size="sm"
                                                    variant="tab"
                                                >
                                                    Product Description
                                                </Button>
                                            </li>
                                            <li
                                                className={`tw-border-b tw-list-none${
                                                    isTechSpecTabOpen
                                                        ? ' tw-border-t tw-border-x tw-border-b-white [&_button]:!tw-text-gray-500'
                                                        : ''
                                                }`}
                                            >
                                                <Button
                                                    aria-controls="tech"
                                                    aria-selected="false"
                                                    data-toggle="tab"
                                                    id="tech-tab"
                                                    onClick={() => {
                                                        location.assign('#tech');
                                                        setIsTechSpecTabOpen(true);
                                                        setIsDetailTabOpen(false);
                                                        setIsProdDescTabOpen(false);
                                                    }}
                                                    size="sm"
                                                    variant="tab"
                                                >
                                                    Technical Specifications
                                                </Button>
                                            </li>
                                            <li
                                                className={`tw-border-b tw-list-none${
                                                    isDetailTabOpen
                                                        ? ' tw-border-t tw-border-x tw-border-b-white [&_button]:!tw-text-gray-500'
                                                        : ''
                                                }`}
                                            >
                                                <ImpressionTracker
                                                    data={{
                                                        dimension16: ordersService.currentOrderNumber,
                                                        index: 1,
                                                        item_id: itemNumber,
                                                        item_list_name: gaItemListName,
                                                    }}
                                                >
                                                    <Button
                                                        aria-selected="false"
                                                        onClick={() => {
                                                            window.open(`/item/${loadedItem.id}`);
                                                            setIsTechSpecTabOpen(false);
                                                            setIsDetailTabOpen(true);
                                                            setIsProdDescTabOpen(false);
                                                        }}
                                                        size="sm"
                                                        variant="tab"
                                                    >
                                                        View More Details
                                                    </Button>
                                                </ImpressionTracker>
                                            </li>
                                            <div className="tw-flex-grow">
                                                <li className="tw-border-b tw-h-[42px]"></li>
                                            </div>
                                        </ul>
                                        <div className="tw-mt-6">
                                            {isProdDescTabOpen && (
                                                <div
                                                    aria-labelledby="desc-tab"
                                                    className="tw-block"
                                                    id="desc"
                                                    role="tabpanel"
                                                >
                                                    <div>
                                                        <div dangerouslySetInnerHTML={{__html: loadedItem.description}} />
                                                        <ExceptionIcons
                                                            exceptions={loadedItem.exceptions}
                                                            user={user}
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                            {isTechSpecTabOpen && (
                                                <div
                                                    aria-labelledby="tech-tab"
                                                    className="tw-block"
                                                    id="tech"
                                                    role="tabpanel"
                                                >
                                                    {loadedItem.techSpecs?.length > 0 && <TechSpecs item={loadedItem} />}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <Spinner />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    ) : null;
};
